import { useLocation, useParams, useSearch } from '@tanstack/react-router';

/**
 * Returns the route pattern and base params for the read view based on the current location.
 * Used by useReadUrl and keyboard navigation to build read URLs for any bookmark ID.
 */
export function getReadRoute(
  pathname: string,
  routeParams: Record<string, string | undefined>,
): {
  toPattern: string;
  baseParams: Record<string, string>;
} {
  if (pathname.startsWith('/folder/')) {
    const folderId = routeParams.folderId ?? pathname.split('/')[2] ?? '';
    const slug = routeParams.slug;
    return slug === undefined
      ? { toPattern: '/folder/$folderId/read/$id', baseParams: { folderId } }
      : { toPattern: '/folder/$folderId/$slug/read/$id', baseParams: { folderId, slug } };
  }
  if (pathname.startsWith('/archive')) {
    return { toPattern: '/archive/read/$id', baseParams: {} };
  }
  if (pathname.startsWith('/liked')) {
    return { toPattern: '/liked/read/$id', baseParams: {} };
  }
  if (pathname.startsWith('/tags/')) {
    const slug = routeParams.slug ?? pathname.split('/')[2] ?? '';
    return { toPattern: '/tags/$slug/read/$id', baseParams: { slug } };
  }
  if (pathname.startsWith('/videos')) {
    return { toPattern: '/videos/read/$id', baseParams: {} };
  }
  if (pathname.startsWith('/search')) {
    return { toPattern: '/search/read/$id', baseParams: {} };
  }
  return { toPattern: '/read/$id', baseParams: {} };
}

/**
 * Returns the correct route path and params for navigating to a bookmark's read view
 * based on the current location (home, folder, archive, liked, tags, videos).
 */
export function useReadUrl(bookmarkId: string): {
  to: string;
  params: {
    id: string;
    slug?: string;
    folderId?: string;
  };
  search: Record<string, unknown>;
} {
  const { pathname } = useLocation();
  const params = useParams({ strict: false });
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const { toPattern, baseParams } = getReadRoute(pathname, params);
  return { to: toPattern, params: { ...baseParams, id: bookmarkId }, search };
}
